export const EXPERT_CTA_LABEL = "Speak To An Expert";
const SERVICE_PROVIDER_INITIALS = "SP";
export const BOOK_APPOINTMENT_HREF = "/contact#contact-book-appointment";

function normalizeKey(value = "") {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function matchProviderToSubCategory(providers, subCategoryName) {
  if (!Array.isArray(providers) || !subCategoryName?.trim()) return null;
  const target = normalizeKey(subCategoryName);
  return (
    providers.find((row) => normalizeKey(row?.sub_category) === target) ||
    providers.find((row) => {
      const cand = normalizeKey(row?.sub_category || "");
      return cand && (target.includes(cand) || cand.includes(target));
    }) ||
    null
  );
}

export function toWhatsAppUrl(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

function phoneFromArticleRow(articleRow) {
  if (!articleRow || typeof articleRow !== "object") return null;
  const sp = articleRow.service_provider;
  return (
    sp?.company_phone_number ||
    sp?.phone ||
    sp?.phone_number ||
    articleRow.company_phone_number ||
    articleRow.provider_phone ||
    null
  );
}

/** Matched API provider when signed in; else article/sponsor fallback (guests can chat without login). */
export function resolveExpertPhone({
  provider,
  isAuthenticated,
  fallbackSponsor,
  articleRow = null,
}) {
  if (isAuthenticated && provider?.company_phone_number) {
    return provider.company_phone_number;
  }
  return phoneFromArticleRow(articleRow) || fallbackSponsor?.phone || null;
}

export function buildExpertWhatsappUrl({
  provider,
  isAuthenticated,
  fallbackSponsor,
  articleRow = null,
}) {
  const phone = resolveExpertPhone({
    provider,
    isAuthenticated,
    fallbackSponsor,
    articleRow,
  });
  return phone ? toWhatsAppUrl(phone) : null;
}

export function isLegalIndustry(industryId) {
  return industryId === "legal";
}

/** Compact view-model for expert WhatsApp CTAs (article sidebar, hero actions). */
export function buildExpertCtaView({
  provider,
  isAuthenticated,
  fallbackSponsor,
  topicLabel = "",
  articleRow = null,
}) {
  const phone = resolveExpertPhone({
    provider,
    isAuthenticated,
    fallbackSponsor,
    articleRow,
  });
  return {
    displayName: EXPERT_CTA_LABEL,
    trustLine:
      fallbackSponsor?.trustLine ||
      (topicLabel
        ? `Plain-language guidance for ${topicLabel} before you commit.`
        : "Expert guidance for your questions in Ghana."),
    whatsappUrl: phone ? toWhatsAppUrl(phone) : null,
    phone,
  };
}

/**
 * View-model for provider cards on category detail pages.
 */
export function buildProviderCardView({
  industryId,
  provider,
  isAuthenticated,
  fallbackSponsor,
  topicLabel = "",
}) {
  const legal = isLegalIndustry(industryId);
  const hasApiProvider = Boolean(isAuthenticated && provider);
  const phone = resolveExpertPhone({ provider, isAuthenticated, fallbackSponsor });
  const whatsappUrl = phone ? toWhatsAppUrl(phone) : null;

  if (legal) {
    return {
      initials: SERVICE_PROVIDER_INITIALS,
      displayName: EXPERT_CTA_LABEL,
      subtitle: topicLabel || provider?.sub_category || "Legal guidance",
      showVerifiedBadge: false,
      showRating: false,
      showConsultant: false,
      showTrustLine: Boolean(fallbackSponsor?.trustLine),
      trustLine: fallbackSponsor?.trustLine || "Expert guidance for your legal questions in Ghana.",
      responseTime: fallbackSponsor?.responseTime || "Initial review within 24 hours",
      showResponseTime: true,
      phone,
      whatsappUrl,
      email: hasApiProvider ? provider.company_email || provider.email || null : null,
      bookAppointmentHref: BOOK_APPOINTMENT_HREF,
      bookAppointmentLabel: "Book appointment",
      canContact: Boolean(phone),
    };
  }

  return {
    initials: SERVICE_PROVIDER_INITIALS,
    displayName: EXPERT_CTA_LABEL,
    subtitle: topicLabel || fallbackSponsor?.role || "Verified partner",
    showVerifiedBadge: true,
    showRating: true,
    showConsultant: false,
    showTrustLine: true,
    trustLine: fallbackSponsor?.trustLine || "",
    responseTime: fallbackSponsor?.responseTime || "",
    showResponseTime: true,
    phone,
    whatsappUrl,
    email: hasApiProvider ? provider.company_email || provider.email : fallbackSponsor?.email,
    bookAppointmentHref: BOOK_APPOINTMENT_HREF,
    bookAppointmentLabel: "Book appointment",
    canContact: Boolean(phone),
  };
}
